import React, { useState, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

const CAGR = 0.12;
const FALLBACK_YIELD = 0.044; // 5.5% - 20% perf fee above 4% floor

function fmt(v) {
  if (!v && v !== 0) return '—';
  const abs = Math.abs(v);
  if (abs >= 10000000) return `₹${(v/10000000).toFixed(2)} Cr`;
  if (abs >= 100000)   return `₹${(v/100000).toFixed(2)} L`;
  return `₹${Number(Math.round(v)).toLocaleString('en-IN')}`;
}

export default function PledgePlusAnalytics({ summary, onClose, visible }) {
  const portfolioValue  = summary?.portfolio_value  || 0;
  const collateralValue = summary?.collateral_value  || 0;
  const xirr            = summary?.xirr             || 0;

  const effectiveYield  = xirr > 0 ? xirr / 100 : FALLBACK_YIELD;
  const usingActual     = xirr > 0;

  const [extra, setExtra] = useState(0);
  const [years, setYears] = useState(3);
  const chartRef  = useRef(null);
  const chartInst = useRef(null);

  const totalCol   = collateralValue + extra;
  const annualRet  = totalCol * effectiveYield;
  const cumulative = annualRet * years;

  // Portfolio growth chart data
  const labels   = Array.from({ length: years + 1 }, (_, i) => i);
  const seriesA  = labels.map(y => Math.round(portfolioValue * Math.pow(1 + CAGR, y)));
  const seriesB  = labels.map((y, i) => Math.round(seriesA[i] + annualRet * y));

  const finalA   = seriesA[seriesA.length - 1];
  const finalB   = seriesB[seriesB.length - 1];
  const uplift   = finalA > 0 ? ((finalB - finalA) / finalA * 100) : 0;

  useEffect(() => {
    if (!chartRef.current || !visible) return;
    const ctx = chartRef.current.getContext('2d');
    if (chartInst.current) chartInst.current.destroy();
    chartInst.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Without Pledge+',
            data: seriesA.map(v => +(v/100000).toFixed(2)),
            borderColor: '#888780', backgroundColor: 'transparent',
            borderDash: [5,4], tension: 0.3, pointRadius: 0,
          },
          {
            label: 'With Pledge+',
            data: seriesB.map(v => +(v/100000).toFixed(2)),
            borderColor: '#0F6E56', backgroundColor: 'rgba(15,110,86,0.07)',
            fill: true, tension: 0.3, pointRadius: 0,
          }
        ]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { title: { display: true, text: 'Years', font: { size: 11 } }, ticks: { font: { size: 11 } } },
          y: { title: { display: true, text: '₹ L', font: { size: 11 } }, ticks: { font: { size: 11 }, callback: v => '₹' + v + 'L' } }
        }
      }
    });
    return () => { if (chartInst.current) chartInst.current.destroy(); };
  }, [extra, years, visible]);

  const cardStyle = {
    background: '#fff', border: '1px solid #f0e8e5',
    borderRadius: 8, padding: '12px 14px',
  };
  const greenCard = {
    background: '#f0fdf4', border: '1px solid #86efac',
    borderRadius: 8, padding: '12px 14px',
  };

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'flex-end',
      animation: 'fadeIn 0.15s ease',
    }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes slideDown { from { transform: translateY(0); opacity: 1; } to { transform: translateY(100%); opacity: 0; } }
        @keyframes fadeIn  { from { opacity: 0; } to { opacity: 1; } }
        .analytics-panel::-webkit-scrollbar { width: 4px; }
        .analytics-panel::-webkit-scrollbar-thumb { background: #e0d8d5; border-radius: 2px; }
      `}</style>
      <div className="analytics-panel" style={{
        background: '#f8f4f2', width: '100%', maxHeight: '92vh',
        borderRadius: '16px 16px 0 0', overflowY: 'auto',
        animation: 'slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        {/* Header */}
        <div style={{
          position: 'sticky', top: 0, background: '#6B1C2A',
          padding: '16px 24px', borderRadius: '16px 16px 0 0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          zIndex: 10,
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>Pledge+ Analytics</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
              Based on your current portfolio
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: '50%', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            ×
          </button>
        </div>

        <div style={{ padding: '20px 24px', maxWidth: 900, margin: '0 auto' }}>

          {/* Summary row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
            <div style={cardStyle}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Current collateral</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#333' }}>{fmt(collateralValue)}</div>
            </div>
            <div style={cardStyle}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Portfolio value</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#333' }}>{fmt(portfolioValue)}</div>
            </div>
            <div style={{ ...cardStyle, background: '#f0fdf4', border: '1px solid #86efac' }}>
              <div style={{ fontSize: 11, color: '#166534', marginBottom: 4 }}>
                {usingActual ? 'Your actual XIRR' : 'Projected rate (conservative)'}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#166534' }}>
                {usingActual ? `${xirr.toFixed(2)}%` : '5.5%'}
              </div>
              <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                {usingActual ? 'Projection uses this rate' : 'No XIRR yet — using avg'}
              </div>
            </div>
          </div>

          {/* ── Section 1: Return Calculator ── */}
          <div style={{ background: '#fff', border: '1px solid #f0e8e5', borderRadius: 10, padding: '16px 18px', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#6B1C2A', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              1 — Return Calculator
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 13, color: '#555', minWidth: 160 }}>Additional collateral</span>
              <input type="range" min={0} max={100000000} step={500000}
                value={extra} onChange={e => setExtra(Number(e.target.value))}
                style={{ flex: 1, accentColor: '#6B1C2A' }} />
              <span style={{ fontSize: 13, fontWeight: 600, minWidth: 80, textAlign: 'right' }}>
                {extra === 0 ? '₹0' : fmt(extra)}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <span style={{ fontSize: 13, color: '#555', minWidth: 160 }}>Years</span>
              <input type="range" min={1} max={10} step={1}
                value={years} onChange={e => setYears(Number(e.target.value))}
                style={{ flex: 1, accentColor: '#6B1C2A' }} />
              <span style={{ fontSize: 13, fontWeight: 600, minWidth: 80, textAlign: 'right' }}>{years} yr{years > 1 ? 's' : ''}</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
              <div style={cardStyle}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>Total collateral</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#333' }}>{fmt(totalCol)}</div>
              </div>
              <div style={cardStyle}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>Annual Pledge+ return</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#166534' }}>{fmt(annualRet)}</div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                  at {usingActual ? `actual ${xirr.toFixed(2)}%` : 'conservative 5.5%'}
                </div>
              </div>
              <div style={greenCard}>
                <div style={{ fontSize: 11, color: '#166534', marginBottom: 3 }}>Cumulative over {years} yr{years > 1 ? 's' : ''}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#14532d' }}>{fmt(cumulative)}</div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 10 }}>
              {usingActual
                ? `Projection uses your actual XIRR of ${xirr.toFixed(2)}%. Pledge+ returns are paid as cash — not reinvested into portfolio.`
                : 'No positive XIRR on record yet. Using conservative 5.5% average return for projection.'}
            </div>
          </div>

          {/* ── Section 2: Portfolio Growth Chart ── */}
          <div style={{ background: '#fff', border: '1px solid #f0e8e5', borderRadius: 10, padding: '16px 18px', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#6B1C2A', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              2 — Portfolio Growth Chart
            </div>

            <div style={{ display: 'flex', gap: 20, marginBottom: 10, fontSize: 12, color: '#666' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 18, height: 0, borderTop: '2px dashed #888780', display: 'inline-block' }}></span>
                Without Pledge+
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 18, height: 2, background: '#0F6E56', display: 'inline-block' }}></span>
                With Pledge+ (portfolio + cash returns)
              </span>
            </div>

            <div style={{ position: 'relative', height: 220 }}>
              <canvas ref={chartRef} role="img" aria-label="Portfolio growth chart with and without Pledge+ cash returns" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginTop: 14 }}>
              <div style={cardStyle}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>Portfolio only (after {years} yrs)</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#333' }}>{fmt(finalA)}</div>
                <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>at 12% CAGR</div>
              </div>
              <div style={cardStyle}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>Portfolio + Pledge+ cash</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#166534' }}>{fmt(finalB)}</div>
                <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>portfolio + {fmt(cumulative)} cash</div>
              </div>
              <div style={greenCard}>
                <div style={{ fontSize: 11, color: '#166534', marginBottom: 3 }}>Uplift</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#14532d' }}>+{uplift.toFixed(1)}%</div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 8 }}>
              Portfolio grows at 12% CAGR independently. Pledge+ returns are additional cash income — not reinvested.
            </div>
          </div>

          {/* ── Section 3: Pledge+ vs LAS ── */}
          <div style={{ background: '#fff', border: '1px solid #f0e8e5', borderRadius: 10, padding: '16px 18px', marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#6B1C2A', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              3 — Pledge+ vs Loan Against Securities
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ border: '2px solid #6B1C2A', borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6B1C2A', background: '#f5eeee', display: 'inline-block', padding: '3px 10px', borderRadius: 6, marginBottom: 12 }}>
                  Pledge+
                </div>
                {[
                  ['Cost of funds', 'None', '#166534'],
                  ['Return on collateral', usingActual ? `${xirr.toFixed(2)}% (your actual)` : '5.5% (avg)', '#166534'],
                  ['Net cash flow', 'Positive every year', '#166534'],
                  ['Shares sold?', 'No', '#333'],
                ].map(([label, value, color]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '0.5px solid #f0e8e5', fontSize: 12 }}>
                    <span style={{ color: '#666' }}>{label}</span>
                    <span style={{ fontWeight: 600, color }}>{value}</span>
                  </div>
                ))}
              </div>
              <div style={{ border: '1px solid #f0e8e5', borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#555', background: '#f5f0ee', display: 'inline-block', padding: '3px 10px', borderRadius: 6, marginBottom: 12 }}>
                  Loan Against Securities
                </div>
                {[
                  ['Cost of funds', '9–11% p.a.', '#c0392b'],
                  ['Return on collateral', 'None', '#888'],
                  ['Net cash flow', 'Negative (interest outgo)', '#c0392b'],
                  ['Shares sold?', 'No', '#333'],
                ].map(([label, value, color]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '0.5px solid #f0e8e5', fontSize: 12 }}>
                    <span style={{ color: '#666' }}>{label}</span>
                    <span style={{ fontWeight: 600, color }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}